import { useState } from "react";
import { useCompanyComments } from "@/hooks/companies/useCompanyComments";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageSquare, PlusCircle, Trash2, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getUserDisplayName } from "@/utils/userUtils";
import { AddCommentModal } from "@/components/candidates/AddCommentModal";
import { CommentContent } from "@/components/comments/CommentContent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CompanyCommentsProps {
  companyId: string;
}

export const CompanyComments = ({ companyId }: CompanyCommentsProps) => {
  const { comments, loading, addComment, deleteComment, updateComment } = useCompanyComments(companyId);
  const [isAddCommentOpen, setIsAddCommentOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<any>(null);
  const [commentToEdit, setCommentToEdit] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();

  const handleAddComment = async (content: string) => {
    await addComment(content);
    setIsAddCommentOpen(false);
  };

  const handleEditComment = async (content: string) => {
    if (!commentToEdit) return;
    await updateComment(commentToEdit.id, content);
    setCommentToEdit(null);
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteComment(commentToDelete.id);
      setCommentToDelete(null);
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-sm shadow">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t("companies.detail.tabs.comments")}</h3>
          <Button 
            onClick={() => setIsAddCommentOpen(true)}
            className="flex items-center gap-1"
          >
            <PlusCircle className="h-4 w-4" />
            <span>{t("companies.comments.addComment")}</span>
          </Button>
        </div>

        
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-sm"></div>
            ))}
          </div>
        ) : comments.length > 0 ? (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-sm">
                <div className="flex justify-between mb-2">
                  <span className={`font-medium ${getUserDisplayName(comment).isItalic ? 'italic' : ''}`}>
                    {getUserDisplayName(comment).name}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(comment.created_at).toLocaleString()}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCommentToEdit(comment)}
                      className="h-6 w-6 p-0 text-gray-400 hover:text-blue-500"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCommentToDelete(comment)}
                      className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CommentContent content={comment.content} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 italic text-center py-8">{t("companies.comments.noComments", "No comments yet")}</p>
        )}

        <AddCommentModal
          isOpen={isAddCommentOpen}
          onClose={() => setIsAddCommentOpen(false)}
          onSave={handleAddComment}
        />

        <AddCommentModal
          isOpen={!!commentToEdit}
          onClose={() => setCommentToEdit(null)}
          onSave={handleEditComment}
          initialContent={commentToEdit?.content || ""}
          isEditing={true}
        />

        <AlertDialog open={!!commentToDelete} onOpenChange={(open) => !open && setCommentToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Comment</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this comment? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteComment}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </div>
  );
};


